'use client'

import { useMemo } from 'react'
import clsx from 'clsx'
import Image from 'next/image'
import Link from 'next/link'

import { LoginModal } from 'modules/auth'
import { DateOfBirthModal } from 'modules/customer'
import { SeoListName } from 'shared/analytics'
import { Tooltip } from 'shared/ui/Tooltip'
import { capitalizeWords } from 'shared/utils/common'
import { isNotNilOrEmpty } from 'shared/utils/ramda'

import useProductCard from '../../hooks/useProductCard'
import { ProductActionButton, ProductReplaceButton } from '../../ports'
import { ProductTypes } from '../../types/products'
import ProductCardBadgesList from '../ProductCard/components/ProductCardBadgesList'
import ProductCardLabelsList from '../ProductCard/components/ProductCardLabelsList'
import ProductCardPrices from '../ProductCard/components/ProductCardPrices'

import shared from '../ProductCard/ProductCard.module.scss'
import styles from './MenuPromotionSmallProductCard.module.scss'

const RECOMMENDS_LIST_NAME = SeoListName.RECOMMENDS_LIST_NAME

type ProductCardProps = {
  productPath: string
  product: ProductTypes
  seoButtonPlace: string
  ageLimitMetCategory?: boolean
  realProductId?: string
  isSubstitute?: boolean
  onClickSeo?: () => void
  idxOnList: number
  id?: string
}

const MenuPromotionSmallProductCard = ({
  productPath,
  ageLimitMetCategory,
  product,
  realProductId,
  isSubstitute,
  onClickSeo,
  seoButtonPlace,
  idxOnList,
  id
}: ProductCardProps): React.ReactNode => {
  const {
    loginModalOpen,
    handleCloseLoginModal,
    dobModalOpen,
    handleCloseDobModal,
    blurProduct,
    blurProductMessage,
    handleBlurredProductClick,
    parsedBadges,
    name,
    productImage,
    nameShort,
    nameDescriptionShort,
    unitDetails,
    pricePerUnit,
    parsedTags,
    maxQuantityForSpecialOffer,
    quantityJump,
    brandPath,
    path
  } = useProductCard({ product, ageLimitMetCategory, productPath })

  const capitalizedShortName = useMemo(
    () => capitalizeWords(nameShort),
    [nameShort]
  )

  return (
    <div className={styles.globalWrapper} id={id}>
      <LoginModal open={loginModalOpen} onClose={handleCloseLoginModal} />
      <DateOfBirthModal open={dobModalOpen} onClose={handleCloseDobModal} />
      {blurProduct && (
        <button
          className={shared.blurredIconWrapper}
          onClick={handleBlurredProductClick}
        >
          <Image
            src='/static/UnderEighteen.svg'
            alt='poniżej 18 lat'
            width={64}
            height={64}
          />
          <div className={shared.blurredInfoWrapper}>{blurProductMessage}</div>
        </button>
      )}
      <div className={clsx(styles.wrapper, { [styles.blur]: blurProduct })}>
        {isNotNilOrEmpty(parsedBadges) && (
          <ProductCardBadgesList
            rootCategorySlug={product.category?.slug}
            badges={parsedBadges}
          />
        )}

        <div className={styles.productDescription}>
          <div className={styles.productDetailsWrapper}>
            <Link className={styles.imageWrapper} href={path} title={name}>
              <Image
                className={styles.styledImage}
                title={name}
                src={productImage}
                alt={name}
                width={64}
                height={64}
                onClick={onClickSeo}
              />
            </Link>
            <div className={shared.basicDetails}>
              {nameShort && (
                <Link href={brandPath || ''} aria-label={capitalizedShortName}>
                  <h4 className={shared.madeBy}>{capitalizedShortName}</h4>{' '}
                </Link>
              )}
              <Link href={path} title={name}>
                <Tooltip
                  id='product-name-tooltip'
                  title={nameDescriptionShort}
                  disableHoverListener={
                    (nameDescriptionShort?.length || 0) < 30
                  }
                >
                  <h3 className={styles.name}>
                    {nameDescriptionShort || name || capitalizedShortName}
                  </h3>
                </Tooltip>
              </Link>
            </div>
          </div>
          <div className={shared.grammarWrapper}>
            <p className={shared.grammar}>{unitDetails}</p>
            <p
              className={shared.pricePerGrammar}
              dangerouslySetInnerHTML={{ __html: pricePerUnit }}
            />
          </div>
          {isNotNilOrEmpty(parsedTags) && (
            <Tooltip
              id={`menu-promotion-small-product-card-tooltip-${product.code}`}
              title={
                maxQuantityForSpecialOffer
                  ? `Maksymalna liczba dostępna w promocji:${maxQuantityForSpecialOffer}`
                  : ''
              }
            >
              <div className={shared.tagsWrapper}>
                <ProductCardLabelsList labels={parsedTags} />
              </div>
            </Tooltip>
          )}

          <ProductCardPrices
            isHovered={false}
            product={product}
            quantityJump={quantityJump}
          />
          <div
            className={clsx(shared.buttonWrapper, 'product-thumbnail-buttons')}
          >
            {isSubstitute ? ( // if they are isSubstitute, that means we operate on modal of replacements
              <ProductReplaceButton
                realProductId={realProductId} // real product
                substitute={product} // replacement
                idxOnList={idxOnList}
              />
            ) : (
              <ProductActionButton
                variant='primaryOutline'
                product={product}
                quantityJump={quantityJump}
                disabled={blurProduct}
                seoButtonPlace={seoButtonPlace}
                idxOnList={idxOnList}
                listName={RECOMMENDS_LIST_NAME}
                size='small'
              />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default MenuPromotionSmallProductCard
